/**
 * Zod schema for `packages/client/public/assets/scenes/world.json` (version 2).
 * Round-21 Phase A/B/F. Shared by:
 *   - the Diorama Editor's dev-save middleware (vite-plugins/devSavePlugin.ts)
 *   - the `pnpm validate:world` CLI
 *
 * This is a SHAPE contract only. It answers "is this a world file?", not "is
 * this a good world?" -- the design rules (parallax budget, occlusion, dead
 * edges, hotspots off the walk band) live in `../world/worldRules.ts`, which is
 * a pure module with no dependencies so it can be unit-tested in milliseconds
 * and reused verbatim inside the editor overlay.
 *
 * Deliberately permissive in two places:
 *   - effect `params` is an open record. The whole point of the effect registry
 *     is that a new effect is one file with its own schema; a closed union here
 *     would mean editing this file every time someone adds an effect, which is
 *     exactly the coupling round 21 exists to remove.
 *   - unknown top-level node keys are allowed through, so an engine can ship a
 *     field before the schema catches up rather than the other way round.
 */
import { z } from 'zod';
const Point = z.tuple([z.number(), z.number()]);
export const WalkBandSchema = z.object({
    xMin: z.number(),
    xMax: z.number(),
    yMin: z.number(),
    yMax: z.number(),
    /** Optional non-rectangular walkable region. When present it replaces the
     *  rect as the REGION the walker may stand in; the rect stays as its
     *  bounding box (edge-walk travel and design rules read the rect). */
    poly: z.array(Point).min(3).optional(),
});
/** Perspective ground quad, corners ordered far-left, far-right, near-right,
 *  near-left. The engine inverse-bilinearly maps a ground point to a depth
 *  v (0 at the far edge, 1 at the near edge) and scales the avatar
 *  farScale..nearScale — depth that can vary with x as well as y. */
export const FloorPlaneSchema = z.object({
    quad: z.tuple([Point, Point, Point, Point]),
    farScale: z.number().positive(),
    nearScale: z.number().positive(),
});
export const WalkBandOverrideSchema = z.object({
    xMin: z.number().optional(),
    xMax: z.number().optional(),
    yMin: z.number().optional(),
    yMax: z.number().optional(),
});
export const EdgeGeomSchema = z.object({
    entryX: z.number().optional(),
    entryY: z.number().optional(),
    pathX: z.number().optional(),
    pathY: z.number().optional(),
});
const EdgeLinkSchema = z.object({
    to: z.string().min(1),
    xMin: z.number().optional(),
    xMax: z.number().optional(),
    yMin: z.number().optional(),
    yMax: z.number().optional(),
});
export const HotspotSchema = z.object({
    id: z.string().min(1),
    at: Point,
    r: z.number().positive(),
    walkTo: Point.nullable().optional(),
    name: z.string(),
    behold: z.string().optional(),
    touch: z.string().optional(),
    ponder: z.string().optional(),
});
export const LayerSchema = z.object({
    id: z.string().min(1),
    from: z.string().min(1),
    clip: z.array(Point).optional(),
    blur: z.number().nonnegative().optional(),
    tintOver: z.string().optional(),
    hole: z.object({
        x: z.number(), y: z.number(),
        sx: z.number().optional(), sy: z.number().optional(),
        r0: z.number(), r1: z.number(),
        stops: z.array(z.tuple([z.number(), z.number()])),
    }).optional(),
});
export const EffectInstanceSchema = z.object({
    kind: z.string().min(1),
    id: z.string().optional(),
    layer: z.string().optional(),
    enabled: z.boolean().optional(),
    params: z.record(z.string(), z.unknown()).optional(),
});
export const CameraSchema = z.object({
    parallaxX: z.number(),
    parallaxY: z.number(),
    overscan: z.object({
        tx: z.number(), ty: z.number(), sx: z.number(), sy: z.number(),
    }).optional(),
});
/**
 * `map` is CARTOGRAPHIC and display-only (round-21 Phase E). It is not a
 * position in any gameplay space and must never be used to compute travel --
 * `edges` is the only navigational truth. `pinned` marks a coordinate a human
 * placed by hand, which scripts/world/gen-map-coords.mjs will not overwrite.
 */
export const MapCoordSchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    pinned: z.boolean().optional(),
});
export const WorldNodeSchema = z.looseObject({
    id: z.string().min(1),
    region: z.string(),
    name: z.string(),
    chain: z.number().optional(),
    render: z.enum(['plate', 'exterior', 'interior', 'forest', 'composed']),
    plate: z.string().optional(),
    walk: WalkBandSchema,
    walkBandOverrides: z.array(WalkBandOverrideSchema).optional(),
    scaleCurve: z.array(z.tuple([z.number(), z.number()])).min(1).optional(),
    scaleClamp: z.tuple([z.number(), z.number()]).optional(),
    floorPlane: FloorPlaneSchema.optional(),
    edges: z.record(z.string(), z.union([EdgeLinkSchema, z.array(EdgeLinkSchema)])),
    geom: z.record(z.string(), EdgeGeomSchema),
    ambience: z.object({
        tint: z.string().optional(),
        fxMode: z.string().optional(),
        musicKey: z.string().optional(),
    }),
    tag: z.string().optional(),
    intro: z.string().optional(),
    hotspots: z.array(HotspotSchema),
    doorSpawn: z.object({
        pos: Point, target: Point, facing: z.number(),
    }).optional(),
    map: MapCoordSchema.optional(),
    unreachable: z.boolean().optional(),
    // composed-scene fields
    show: z.array(z.string()).optional(),
    canvas: z.string().optional(),
    belowFps: z.number().optional(),
    camera: CameraSchema.optional(),
    layers: z.array(LayerSchema).optional(),
    effects: z.array(EffectInstanceSchema).optional(),
    // audio beds, moved off `render` in Phase B
    sfxBed: z.enum(['wind', 'forestair', 'fire']).optional(),
    surface: z.enum(['ground', 'loam', 'wood']).optional(),
});
export const WorldFileSchema = z.object({
    version: z.literal(2),
    note: z.string().optional(),
    nodes: z.array(WorldNodeSchema).min(1),
});
//# sourceMappingURL=worldFile.schema.js.map